import { atom } from "jotai";
import { atomWithStorage } from "jotai/utils";
import {
  AudioPlaySource,
  AudioType,
  PlaybackEventSource,
  StudioSpeaker,
  StudioVoiceModel,
  SyncState
} from "../../@types/data";
import {
  Speaker,
  Voice,
  VoiceConversionTypeEnum
} from "../../@types/generated/types";
import type { CatalogControl } from "../../components/studio/Editor/XLPTControls/catalogControls";
import { speakersToCharactersFactory } from "../factories/studio.factory";
import {
  SegmentsByIdType,
  StudioDefaultSpeakerType,
  StudioMetadataType
} from "../types/studio.types";
import { atomWithToggle } from "../utils/atomCreators.utils";

export const isVideoPlayingAtom = atomWithToggle(false);
export const areSourceSegmentsDisplayedAtom = atom<boolean>(false);
export const areAudibleOverlapsMutedAtom = atom<boolean>(false);
// Per-character mute state, keyed by getCharacterKey() in Timeline/utils.ts:
// composite (speakerCode, name) in legacy mode since speakerCode alone is not
// unique, the character id in catalog mode. Client-side only; resets on reload.
export const mutedSpeakerKeysAtom = atom<Record<string, boolean>>({});
// Master toggle for the speaker lanes feature (per-character timeline rows +
// per-speaker mute/solo controls). When off, the feature is fully hidden and
// has no effect on playback.
export const speakerLanesEnabledAtom = atomWithToggle(false);
// Open state for the "retranslate all segments" confirmation dialog. Kept as a
// local atom (rather than the URL-query modal stack) so open/close is
// synchronous and instant — the dialog must be able to close immediately,
// before the retranslate mutation fires.
export const confirmRetranslateAllSegmentsModalOpenAtom = atomWithToggle(false);
// Message for the dialog shown after a linguist approves their stage. Lives
// outside Studio (dialog is mounted in Providers) because Studio redirects to
// My Tasks right after the transition, and the dialog must survive that.
export const stageCompletedMessageAtom = atom<{
  title: string;
  description: string;
} | null>(null);
// Track split/merge operations to prevent scroll jumps
export const segmentOperationInProgressAtom = atom<boolean>(false);
export const musicAndEffectsTrackAudioAtom = atom<string | undefined>(
  undefined
);
export const musicAndEffectsVolumeAtom = atom<number>(0);

export const studioWaveformAtom = atom<number[]>([]);

export const globalVoiceConversionTypeAtom = atom<VoiceConversionTypeEnum>(
  VoiceConversionTypeEnum.Advc
);

export const pipStateAtom = atom<boolean>(false);
export const scrollTopAtom = atom<number>(0);
export const sourceVolumeAtom = atom<number>(1);
// Playback volume (0–1) for the translated/target audio. The playback engine
// applies it to its translated voices through the mix, mirroring how
// sourceVolumeAtom drives the source volume.
export const translatedVolumeAtom = atom<number>(1);
export const syncStateAtom = atom<SyncState>("Saved");
export const syncStateErrorAtom = atom<string | null>(null);
export const audioLanguageAtom = atom<AudioType>("source");
// When enabled, timeline playback plays BOTH the source and the target
// (translated) audio at once, regardless of audioLanguageAtom. It overrides the
// mute side of the language selector: source stays unmuted and the per-segment
// translated audio plays just as it does in "translated" mode.
export const playBothLanguagesAtom = atomWithToggle(false);
// True while the CURRENT segment-scoped source playback should also sound the
// translated audio — set by the play/stop-segment shortcut when both language
// pills are selected. Explicit row-button clicks keep it false, so they stay
// exclusive to their own language. Only meaningful in combination with
// audioPlaySource === "segment-source".
export const segmentPlayBothLanguagesAtom = atom<boolean>(false);
export const isTimelineMaxDimensionAtom = atomWithToggle(true);
export const zoomValueAtom = atom<number>(50);
export const audioPlaySourceAtom = atom<AudioPlaySource>("timeline");

/**
 * Continuous timeline playback is running, as opposed to a single-segment play
 * or no playback at all. Both atoms matter: the play source can change while
 * playback continues.
 */
export const isTimelinePlaybackAtom = atom<boolean>(
  get => get(isVideoPlayingAtom) && get(audioPlaySourceAtom) === "timeline"
);
export type ComposerPlaybackRequest = {
  id: number;
  startTime: number;
  endTime: number;
};
export const composerPlaybackRequestAtom = atom<ComposerPlaybackRequest | null>(
  null
);
export const composerPlaybackStartedRequestIdAtom = atom<number | null>(null);
export const composerPlaybackCancelledRequestIdAtom = atom<number | null>(null);

/**
 * Auto-centering of the current segment during timeline playback.
 * Suspended when the user scrolls the segments list mid-playback; re-armed
 * by the "Sync to media" button, by an explicit segment selection, or when
 * timeline playback stops.
 */
export const isPlaybackAutoScrollEnabledAtom = atom<boolean>(true);

export const LIST_AUTO_SYNC_STORAGE_KEY = "studio.listAutoSync";

/**
 * User preference for how the segments list follows timeline playback:
 * automatic (the list centres the playing segment) or manual (the list stays
 * where the user left it and only the "Sync to media" button moves it).
 */
export const isListAutoSyncPreferredAtom = atomWithStorage<boolean>(
  LIST_AUTO_SYNC_STORAGE_KEY,
  true,
  undefined,
  { getOnInit: true }
);

/** Visible row window of the segments list, published from onRowsRendered. */
export const visibleRowRangeAtom = atom<{
  startIndex: number;
  stopIndex: number;
}>({ startIndex: 0, stopIndex: 0 });

/** Row index (segment index + numEditsBeforeRow offset) of the playing segment. */
export const currentSegmentRowIndexAtom = atom<number | undefined>(undefined);

/** Where the playing segment sits relative to the viewport. */
export const playbackSyncDirectionAtom = atom<"up" | "down" | "visible">(
  get => {
    const rowIndex = get(currentSegmentRowIndexAtom);
    if (rowIndex === undefined) return "visible";
    const { startIndex, stopIndex } = get(visibleRowRangeAtom);
    if (rowIndex < startIndex) return "up";
    if (rowIndex > stopIndex) return "down";
    return "visible";
  }
);

/** Bumped by "Sync to media" to request an immediate re-centre. */
export const syncToVideoRequestAtom = atom<number>(0);

export const mediaDurationAtom = atom<number>(-1);
export const previewModeAtom = atomWithToggle(false);
export const isRequestingSpeechAtom = atom<boolean>(false);
// Track the source and timestamp of the most recent user-initiated play request.
// Used by Timeline.tsx's wavesurfer "play" guard to avoid suppressing legitimate
// play events that arrive within 200ms of a pause (the PiP-artifact window).
export const lastPlayEventAtom = atom<{
  source: PlaybackEventSource;
  timestamp: number;
}>({ source: "auto", timestamp: 0 });
export const isAudioLoadingAtom = atom<boolean>(false);

// null represents the default "all characters selected" state. An explicit
// array represents a narrowed timeline-only character selection.
//
// Raw selection state — only the filter UI itself should read this. Everything
// that acts on the filter (timeline visibility, per-segment translated audio,
// speaker lanes) must read effectiveTimelineCharacterFilterAtom instead.
export const timelineCharacterFilterAtom = atom<string[] | null>(null);

// The character filter's UI lives inside the speaker-lanes panel, which only
// renders on a maximized timeline with lanes enabled — and lanes get
// force-disabled when the timeline is minimized or the Transcription stage is
// entered. Shared by the atoms that gate on that visibility so they cannot
// drift apart.
export const isCharacterFilterVisibleAtom = atom<boolean>(
  get => get(isTimelineMaxDimensionAtom) && get(speakerLanesEnabledAtom)
);

// The character filter as it should actually be applied. Applying the raw
// selection while the UI is off screen would leave segments filtered out of the
// timeline and their translated audio muted with no visible control to explain
// or undo it, so collapse to the "all characters" sentinel whenever the filter
// UI is not on screen. The raw selection is preserved, so it comes back when
// the panel does.
export const effectiveTimelineCharacterFilterAtom = atom<string[] | null>(
  get =>
    get(isCharacterFilterVisibleAtom) ? get(timelineCharacterFilterAtom) : null
);

// Copied from the `voice_catalog` flag by StudioFeatureFlagProvider. An atom
// rather than the flag context so derived atoms that resolve characters
// recompute when it changes, and store actions can read it outside React.
export const isVoiceCatalogEnabledAtom = atom<boolean>(false);
export const isVoiceCatalogFlagLoadedAtom = atom<boolean>(false);
// In catalog mode, true until STUDIO_SPEAKERS_CATALOG answers with data or an
// error, and STUDIO_VOICE_MODELS too when a voice is cast. Read from the
// requests, since a channel can have no characters to carry the catalog.
export const isSpeakersCatalogLoadingAtom = atom<boolean>(false);
// By cast voice id; a voice missing here has unknown models.
export const voiceModelsAtom = atom<Record<string, StudioVoiceModel[]>>({});
// Every row reads it, so Studio keeps the array on an equal reload.
export const catalogMarkupControlsAtom = atom<CatalogControl[]>([]);
export const isFindInSegmentsModalOpenAtom = atom<boolean>(false);
export const isShortcutsHelperModalOpenAtom = atom<boolean>(false);
export const isQaPerformanceModalOpenAtom = atom<boolean>(false);
export const sourceTextSelectionAtom = atom<{
  term: string;
  mousePositionX: number;
}>({
  term: "",
  mousePositionX: 0
});

export const fetchSegmentsLoadingAtom = atom<boolean>(false);
export const fetchSegmentsErrorAtom = atom<boolean>(false);

// Wavesurfer seek time (seconds) that landed outside the currently-loaded segment batch.
// Set by the Timeline seek handler when no segment covers the position;
// cleared by useScrollOnTimelineChange once segments for that time are loaded and selected.
/**
 * A seek whose target segment is not loaded yet, held until the batches that
 * cover it have been fetched. Carries whether the user drove the seek, because
 * the intent has to survive the fetch: the deferred half of a *user* seek is a
 * deliberate selection, while the playhead crossing a batch boundary is not.
 */
export type PendingSeek = {
  time: number;
  isUserSeek: boolean;
};

const seekPendingValueAtom = atom<PendingSeek | null>(null);

/**
 * Writes that say the same thing as the current value are dropped, so the
 * consumer effect in useScrollOnTimelineChange re-runs only on a real change.
 * It used to hold a bare number and get that for free; a fresh object per write
 * would make it fetch the next batch twice for a repeated seek.
 *
 * A write at the time already pending cannot take `isUserSeek` away either. The
 * media element re-emits `seeking` for a seek still in flight — measured at
 * ~850ms after the pointer was released, well past USER_SEEK_GRACE_MS — so
 * `isUserInitiatedSeek` reports false for what is the same seek. Letting that
 * land would cost the seek both its lane reveal (ADR 0003) and, while batches
 * are still being fetched for it, the loading overlay held up by
 * awaitingSegmentsAtom.
 */
export const seekPendingAtom = atom(
  get => get(seekPendingValueAtom),
  (get, set, next: PendingSeek | null) => {
    const prev = get(seekPendingValueAtom);
    if (prev === next) return;
    if (prev && next && prev.time === next.time) {
      if (prev.isUserSeek || !next.isUserSeek) return;
    }
    set(seekPendingValueAtom, next);
  }
);

/**
 * True while a jump is still fetching the batches its target sits in.
 *
 * The jump loop has no pending-seek to hold the overlay open, so before this
 * it rode on `fetchSegmentsLoadingAtom` alone and the overlay flashed once per
 * batch. Raised by useScrollOnTimelineChange when it starts fetching for a
 * target it cannot find, lowered when the target lands or the request is
 * retired.
 */
export const jumpFetchPendingAtom = atom<boolean>(false);

/**
 * True while the studio is making the user wait on segments: a batch request is
 * in flight, a seek the user drove has not landed yet, or a jump is still
 * fetching its target.
 *
 * `fetchSegmentsLoadingAtom` alone flips per request, and the batches on the
 * way to a jump target are not fetched back-to-back — the segments they produce
 * are published by an async effect — so a loading overlay driven by it flashes
 * once per batch. Holding it open for the whole navigation collapses that
 * cascade into one continuous wait.
 *
 * The playhead running past what is loaded during playback also sets a pending
 * seek, but with `isUserSeek: false` (ADR 0003): nobody is waiting on it, so it
 * must not raise a blocking overlay. useScrollOnTimelineChange keeps that true
 * by passing `isUserSeek` to fetchMore, which tops up silently when it is false.
 */
export const awaitingSegmentsAtom = atom(
  get =>
    get(fetchSegmentsLoadingAtom) ||
    get(jumpFetchPendingAtom) ||
    get(seekPendingAtom)?.isUserSeek === true
);

export const totalSegmentsCountAtom = atom<number>(0);
export const doneSegmentsCountAtom = atom<number>(0);
export const workableDurationSegmentCountAtom = atom<number>(0);

// all speakers on channel
export const speakersAtom = atom<StudioSpeaker[]>([]);
// all speakers with names
export const charactersAtom = atom<StudioSpeaker[]>(get =>
  speakersToCharactersFactory(get(speakersAtom))
);

export type VideoCharacter = Pick<Speaker, "id" | "name" | "colorCode">;

// Characters actually assigned to at least one non-archived segment in the
// current translated video, as reported by the studio query. Kept separate from
// speakersAtom, which is the channel-wide catalogue used by character
// assignment controls. This is a load-time snapshot; consumers should read
// videoCharactersAtom (segment.atom.ts), which keeps it in sync with in-session
// speaker reassignments.
export const usedCharactersAtom = atom<VideoCharacter[]>([]);

export const studioVoicesAtom = atom<Voice[]>([]);

// Source-audio waveform amplitudes for the whole video, fetched from the
// backend audioWaveformJSONFile (the same data the Timeline renders). The
// timeline wavesurfer is peaks+media based and never decodes audio, so
// features that need a per-segment waveform (e.g. the Split Segment modal)
// slice this array by time proportion instead.
export const sourceWaveformDataAtom = atom<number[]>([]);

export const studioMetadataAtom = atom<StudioMetadataType>({});
// Rows read this, not studioMetadataAtom, which changes on every video read.
export const translationLanguageAtom = atom(
  get => get(studioMetadataAtom).translationLanguage
);
export const studioDefaultSpeakerAtom = atom<StudioDefaultSpeakerType>({});
export const studioSegmentsByIdRefAtom = atom<
  React.MutableRefObject<SegmentsByIdType>
>({} as React.MutableRefObject<SegmentsByIdType>);

export const studioVideoElementAtom = atom<HTMLMediaElement | null | undefined>(
  null
);

export const useDialogueAsSourceAtom = atom<boolean>(false);

export const doesSpaceHaveMultipleMembersAtom = atom<boolean>(false);

export const generatingSegmentsRefAtom = atom<
  React.MutableRefObject<
    Record<string, boolean | { isRequesting: boolean }>
  > & {
    generationTracking?: "row" | "session";
  }
>({ current: {} });
